import type { CollectionItem } from '@/data/schema/collection-item'
import type { ContractItem } from '@/data/schema/contract-item'
import type { JourneyItem } from '@/data/schema/journey-item'
import { ref, computed, watch } from 'vue'
import type { Ref } from 'vue'
import type { Params, Row } from '@/data/types/datatable'

type CoItem = ContractItem | CollectionItem | JourneyItem

/**
 * Base table configuration
 * @returns
 */
export function useDatatable() {
	const theme = '#082f49'
	const items = ref<Row[]>([])
	const total = ref<number>(0)

	// Sort params
	const params = ref<Params>({
		page: 1,
		pageCount: 1,
		rowsPerPage: 10,
		sortBy: undefined,
		sortType: undefined
	})

	// Sort param concat
	const sortBy = computed(() => {
		if (!params.value.sortBy) {
			return null
		}

		return `${params.value.sortBy}:${params.value.sortType ?? 'asc'}`
	})

	return { theme, items, total, params, sortBy }
}

export function getStatusClass(status: string) {
	if (status === 'Action Required' || status === 'Warning' || status === 'Load Warning') {
		return 'danger'
	} else if (status === 'Complete' || status === 'Load Complete') {
		return 'success'
	} else if (status === 'Skipped') {
		return 'outline'
	} else if (status === 'In Progress' || status === 'Load In Progress') {
		return 'warning'
	} else {
		return 'info'
	}
}

/**
 * Primarily used for kit tables in expandable rows,
 * this function tells a child table to match column widths
 * to a parent table.
 * @param parent ref
 * @param child ref
 * @param exclude array of column indexes to ignore if required (typically [0])
 */
export function useMatchColumnWidths(parent: any, child: any, exclude: number[]) {
	const parentColumns = parent.$el.querySelectorAll('th')
	const childColumns = child.$el.querySelectorAll('td')

	let parentIndex = 0
	for (let index = 0; index < parentColumns.length; index++) {
		if (!exclude.includes(index)) {
			const width = parentColumns[index].offsetWidth
			if (childColumns[parentIndex]) {
				childColumns[parentIndex].style.width = `${width}px`
			}
			parentIndex++
		}
	}
}

/**
 * Watch the selected items and handle any corresponding
 * kit components when an item of type "kit" is checked
 * or unchecked
 * @param selectedItems
 * @param selectedComponents
 */
export function useSelectedItemsHandler(
	selectedItems: Ref<CoItem[]>,
	selectedComponents: Ref<CoItem[]>
) {
	const handleSelectedItemsChange = async (newValue: CoItem[], oldValue: CoItem[]) => {
		// These will get specifically the items that have been
		// added or removed, to prevent cross-contamination
		const addedItems = newValue.filter(
			(item) => !oldValue.some((oldItem) => oldItem.id === item.id)
		)
		const removedItems = oldValue.filter(
			(item) => !newValue.some((newItem) => newItem.id === item.id)
		)

		// handles removal of kit components
		removedItems.forEach((removedItem) => {
			removedItem.kit_components?.forEach((removedComponent) => {
				selectedComponents.value = selectedComponents.value.filter(
					(selectedComponent) => selectedComponent.id !== removedComponent.id
				)
			})
		})

		// handles adding of kit components
		for (const item of addedItems) {
			if (item.type === 'Kit' && item.kit_components) {
				// get any components not already selected
				const filteredComponents = item.kit_components.filter(
					(comp) => !selectedComponents.value.some((sc) => sc.id === comp.id)
				)

				// Push them to selected components
				selectedComponents.value.push(...filteredComponents)
			}
		}

		selectedComponents.value = [...selectedComponents.value]
	}

	// Track previous items as native watch oldValue
	// wasnt working as expected
	let previousItems = [...selectedItems.value]

	watch(
		selectedItems,
		async (newValue) => {
			const oldValue = [...previousItems]
			await handleSelectedItemsChange(newValue, oldValue)
			previousItems = [...newValue]
		},
		{ deep: true }
	)
}
